import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Pencil, Plus, Trash2, Layers, Package, Settings as SettingsIcon, Receipt, Users as UsersIcon, Server, Check, X, RefreshCw, Upload, Image as ImageIcon, ShieldCheck, Ban, LayoutDashboard, WalletCards, CreditCard, RotateCcw } from "lucide-react";
import { API } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { ChangePasswordForm } from "../components/ChangePasswordForm";

const iconOptions = ["tiktok", "instagram", "youtube", "discord", "facebook", "twitter", "telegram"];

const emptyCat = { name: "", slug: "", icon: "instagram", order: 0 };
const emptySvc = {
  service_code: 1000,
  category_id: "",
  name: "",
  speed: "Fast",
  quality: "High",
  geo: "Worldwide",
  start_time: "0-1 hour",
  min_qty: 10,
  max_qty: 10000,
  price_per_1000: 0,
  description: "",
  warning: "",
  active: true,
  provider_service_id: "",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [topups, setTopups] = useState([]);
  const [users, setUsers] = useState([]);
  const [providerBalance, setProviderBalance] = useState(null);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const [catDlg, setCatDlg] = useState({ open: false, edit: null, data: emptyCat });
  const [svcDlg, setSvcDlg] = useState({ open: false, edit: null, data: emptySvc });
  const [orderActionDlg, setOrderActionDlg] = useState({ open: false, action: null, order: null });
  const [orderActionLoading, setOrderActionLoading] = useState(false);

  const load = async () => {
    await Promise.all([
      api.get("/categories").then(({ data }) => setCategories(data)),
      api.get("/admin/services")
        .catch(() => ({ data: [] }))
        .then(({ data }) => setServices(data)),
      api.get("/admin/settings")
        .catch(() => api.get("/settings"))
        .then(({ data }) => setSettings(data)),
      api.get("/admin/orders")
        .catch(() => ({ data: [] }))
        .then(({ data }) => setOrders(data)),
      api.get("/admin/topups")
        .catch(() => ({ data: [] }))
        .then(({ data }) => setTopups(data)),
      api.get("/admin/users")
        .catch(() => ({ data: [] }))
        .then(({ data }) => setUsers(data)),
    ]);
  };

  useEffect(() => {
    if (user && user !== false) load();
    if (!user || user === false) return undefined;
    const interval = window.setInterval(load, 120000);
    return () => window.clearInterval(interval);
  }, [user]);

  if (loading) return null;
  if (!user || user === false || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  // ---- Categories ----
  const openNewCat = () => setCatDlg({ open: true, edit: null, data: emptyCat });
  const openEditCat = (c) => setCatDlg({ open: true, edit: c.id, data: { ...c } });

  const saveCat = async () => {
    try {
      const payload = {
        name: catDlg.data.name,
        slug: catDlg.data.slug,
        icon: catDlg.data.icon,
        order: Number(catDlg.data.order) || 0,
      };
      if (catDlg.edit) await api.put(`/admin/categories/${catDlg.edit}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Kategori disimpan");
      setCatDlg({ ...catDlg, open: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const delCat = async (c) => {
    if (!window.confirm(`Padam kategori "${c.name}"? Servis dalam kategori ini juga akan dipadam.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Kategori dipadam");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Services ----
  const openNewSvc = () => setSvcDlg({
    open: true, edit: null,
    data: { ...emptySvc, category_id: categories[0]?.id || "" },
  });
  const openEditSvc = (s) => setSvcDlg({ open: true, edit: s.id, data: { ...s } });

  const saveSvc = async () => {
    try {
      const d = svcDlg.data;
      const payload = {
        service_code: Number(d.service_code),
        category_id: d.category_id,
        name: d.name,
        speed: d.speed,
        quality: d.quality,
        geo: d.geo,
        start_time: d.start_time,
        min_qty: Number(d.min_qty),
        max_qty: Number(d.max_qty),
        price_per_1000: Number(d.price_per_1000),
        description: d.description,
        warning: d.warning,
        active: !!d.active,
        provider_service_id: (d.provider_service_id || "").toString(),
      };
      if (svcDlg.edit) await api.put(`/admin/services/${svcDlg.edit}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Servis disimpan");
      setSvcDlg({ ...svcDlg, open: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const delSvc = async (s) => {
    if (!window.confirm(`Padam servis "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/services/${s.id}`);
      toast.success("Servis dipadam");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Provider actions ----
  const approveTopup = async (id) => {
    try {
      await api.post(`/admin/topups/${id}/approve`);
      toast.success("Top up diapprove & baki ditambah");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rejectTopup = async (id) => {
    const note = window.prompt("Sebab reject (optional):") || "";
    try {
      const fd = new FormData();
      fd.append("note", note);
      await api.post(`/admin/topups/${id}/reject`, fd);
      toast.success("Top up direject");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const refreshOrder = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/refresh`);
      toast.success("Status diupdate");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openOrderAction = (action, order) => setOrderActionDlg({ open: true, action, order });
  const confirmOrderAction = async () => {
    const { action, order } = orderActionDlg;
    if (!action || !order) return;
    setOrderActionLoading(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/${action}`);
      toast.success(action === "cancel"
        ? `Order dibatalkan. Refund sebenar RM ${Number(data.refunded || 0).toFixed(2)}.`
        : data.message || "Order direfund");
      setOrderActionDlg({ open: false, action: null, order: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || `Gagal ${action} order`);
    } finally {
      setOrderActionLoading(false);
    }
  };
  const fetchProviderBalance = async () => {
    try {
      const { data } = await api.get("/admin/provider/balance");
      setProviderBalance(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const adjustUserBalance = async (u) => {
    const raw = window.prompt(`Adjust baki untuk ${u.email}. Masukkan +/- amount (contoh: 20 atau -5):`);
    if (raw == null) return;
    const amt = parseFloat(raw);
    if (isNaN(amt)) return toast.error("Amount tidak sah");
    const note = window.prompt("Nota (optional):") || "";
    try {
      const fd = new FormData();
      fd.append("amount", String(amt));
      fd.append("note", note);
      await api.post(`/admin/users/${u.id}/adjust-balance`, fd);
      toast.success("Baki diupdate");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadQr = async (file) => {
    if (!file) return;
    setUploadingQr(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/upload/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("QR TNG diupload");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingQr(false); }
  };

  // ---- Settings ----
  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "-";
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <div className="border-b border-white/5 bg-[#0a0a0c]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-0.5">
            <span className="font-heading font-black text-xl tracking-tighter text-white">AZMIEY</span>
            <span className="font-heading font-black text-xl tracking-tighter text-violet-500">STORE</span>
            <span className="ml-3 text-[10px] uppercase tracking-widest text-zinc-500">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-zinc-500">{user.email}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm border border-white/10 bg-white/[0.03] hover:bg-white/[0.08]"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-[1500px] mx-auto px-4 py-6 lg:px-6 lg:py-8">
        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="h-fit lg:sticky lg:top-24">
            <div className="mb-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-400 mb-2">Workspace</div>
              <h1 className="font-heading text-2xl font-black tracking-tight">Kawalan Store</h1>
            </div>
            <nav className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-1" aria-label="Admin navigation">
              <AdminNavButton Icon={LayoutDashboard} label="Overview" value="overview" activeTab={activeTab} onClick={setActiveTab} />
              <AdminNavButton Icon={Receipt} label="Orders" value="orders" activeTab={activeTab} onClick={setActiveTab} />
              <AdminNavButton Icon={Package} label="Services" value="services" activeTab={activeTab} onClick={setActiveTab} />
              <AdminNavButton Icon={WalletCards} label="Customers & wallet" value="users" activeTab={activeTab} onClick={setActiveTab} />
              <AdminNavButton Icon={CreditCard} label="Payment" value="topups" activeTab={activeTab} onClick={setActiveTab} />
              <AdminNavButton Icon={SettingsIcon} label="Settings" value="settings" activeTab={activeTab} onClick={setActiveTab} />
              <AdminNavButton Icon={Server} label="Provider API" value="api" activeTab={activeTab} onClick={setActiveTab} />
              <AdminNavButton Icon={ShieldCheck} label="Account" value="account" activeTab={activeTab} onClick={setActiveTab} />
            </nav>
          </aside>

          <section className="min-w-0">
            <div className="mb-6 lg:hidden">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-400 mb-2">Dashboard</div>
              <h1 className="font-heading text-3xl font-black tracking-tight">Panel Kawalan</h1>
            </div>

        {/* Stats */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mb-8">
          <StatCard Icon={Layers} label="Kategori" value={categories.length} />
          <StatCard Icon={Package} label="Servis" value={services.length} />
          <StatCard Icon={Receipt} label="Orders" value={orders.length} />
          <StatCard Icon={UsersIcon} label="Users" value={users.length} />
          <StatCard Icon={SettingsIcon} label="WA" value={settings?.wa_number || "-"} mono />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#0f0f12] border border-white/5 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="topups" data-testid="tab-topups">
              Top Ups {topups.filter((t) => t.status === "waiting_payment").length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[9px] rounded bg-amber-500/20 text-amber-300">
                  {topups.filter((t) => t.status === "waiting_payment").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Servis</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Kategori</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api">API</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account"><ShieldCheck className="w-3.5 h-3.5 mr-1" />Akaun</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
              <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold">Recent orders</h3>
                    <p className="text-xs text-zinc-500 mt-1">Latest activity across your store.</p>
                  </div>
                  <button onClick={() => setActiveTab("orders")} className="text-xs text-violet-400 hover:text-violet-300" data-testid="view-all-orders">View all</button>
                </div>
                <div className="grid gap-2">
                  {orders.slice(0, 5).map((o) => (
                    <button key={o.id} onClick={() => setActiveTab("orders")} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-black/20 px-3 py-3 text-left hover:bg-white/[0.04]" data-testid={`overview-order-${o.id}`}>
                      <span className="min-w-0"><span className="block truncate text-sm text-zinc-200">{o.service_name}</span><span className="block truncate text-[11px] text-zinc-500">{o.user_email}</span></span>
                      <span className="shrink-0 text-right"><span className="block font-mono text-sm text-zinc-200">RM {Number(o.cost).toFixed(2)}</span><StatusBadge status={o.status} /></span>
                    </button>
                  ))}
                  {orders.length === 0 && <p className="py-6 text-center text-sm text-zinc-500">No orders yet</p>}
                </div>
              </div>
              <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-5">
                <h3 className="font-heading font-bold">Action centre</h3>
                <p className="text-xs text-zinc-500 mt-1 mb-4">Keep payments and catalogue moving.</p>
                <div className="grid gap-2">
                  <button onClick={() => setActiveTab("topups")} className="flex items-center justify-between rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-3 text-left" data-testid="overview-pending-topups"><span className="text-sm text-zinc-200">Pending top ups</span><span className="font-mono text-amber-300">{topups.filter((t) => t.status === "waiting_payment").length}</span></button>
                  <button onClick={() => setActiveTab("services")} className="flex items-center justify-between rounded-xl border border-violet-500/20 bg-violet-500/5 px-3 py-3 text-left" data-testid="overview-services"><span className="text-sm text-zinc-200">Active services</span><span className="font-mono text-violet-300">{services.filter((s) => s.active).length}</span></button>
                  <button onClick={() => setActiveTab("users")} className="flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-3 text-left" data-testid="overview-customers"><span className="text-sm text-zinc-200">Customers</span><span className="font-mono text-emerald-300">{users.length}</span></button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SERVICES */}
          <TabsContent value="services" className="mt-4">
            <div className="rounded-2xl bg-[#0f0f12] border border-white/5 overflow-hidden mb-6">
              <div className="flex items-center justify-between p-4 border-b border-white/5">
                <h3 className="font-heading font-bold">Senarai Servis</h3>
                <button
                  data-testid="add-service-btn"
                  onClick={openNewSvc}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" /> Tambah Servis
                </button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">ID</TableHead>
                      <TableHead className="text-zinc-500">Nama</TableHead>
                      <TableHead className="text-zinc-500">Kategori</TableHead>
                      <TableHead className="text-zinc-500">Speed</TableHead>
                      <TableHead className="text-zinc-500">Harga/1K</TableHead>
                      <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.map((s) => (
                      <TableRow key={s.id} className="border-white/5" data-testid={`svc-row-${s.service_code}`}>
                        <TableCell className="font-mono text-violet-400">[{s.service_code}]</TableCell>
                        <TableCell className="text-white">{s.name}</TableCell>
                        <TableCell className="text-zinc-400">{catName(s.category_id)}</TableCell>
                        <TableCell className="text-zinc-400">{s.speed}</TableCell>
                        <TableCell className="text-zinc-400">RM {Number(s.price_per_1000).toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <button onClick={() => openEditSvc(s)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`edit-svc-${s.service_code}`}>
                            <Pencil className="w-4 h-4 text-zinc-400" />
                          </button>
                          <button onClick={() => delSvc(s)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`del-svc-${s.service_code}`}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {services.length === 0 && (
                      <TableRow className="border-white/5"><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Tiada servis</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="mt-4">
            <div className="rounded-2xl bg-[#0f0f12] border border-white/5 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-white/5">
                <h3 className="font-heading font-bold">Senarai Kategori</h3>
                <button
                  data-testid="add-category-btn"
                  onClick={openNewCat}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" /> Tambah Kategori
                </button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Nama</TableHead>
                    <TableHead className="text-zinc-500">Slug</TableHead>
                    <TableHead className="text-zinc-500">Icon</TableHead>
                    <TableHead className="text-zinc-500">Order</TableHead>
                    <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((c) => (
                    <TableRow key={c.id} className="border-white/5" data-testid={`cat-row-${c.slug}`}>
                      <TableCell className="text-white">{c.name}</TableCell>
                      <TableCell className="text-zinc-400 font-mono text-xs">{c.slug}</TableCell>
                      <TableCell className="text-zinc-400">{c.icon}</TableCell>
                      <TableCell className="text-zinc-400">{c.order}</TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => openEditCat(c)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`edit-cat-${c.slug}`}>
                          <Pencil className="w-4 h-4 text-zinc-400" />
                        </button>
                        <button onClick={() => delCat(c)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`del-cat-${c.slug}`}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-4">
            <div className="rounded-2xl bg-[#0f0f12] border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-bold">SMM Orders (Balance)</h3>
                  <p className="text-xs text-zinc-500 mt-1">Poller auto-update setiap 2 minit.</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">User</TableHead>
                      <TableHead className="text-zinc-500">Servis</TableHead>
                      <TableHead className="text-zinc-500">Target</TableHead>
                      <TableHead className="text-zinc-500">Qty</TableHead>
                      <TableHead className="text-zinc-500">Cost</TableHead>
                      <TableHead className="text-zinc-500">Refund</TableHead>
                      <TableHead className="text-zinc-500">Provider #</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Remains</TableHead>
                      <TableHead className="text-zinc-500">Start Count</TableHead>
                      <TableHead className="text-zinc-500">Current Count</TableHead>
                      <TableHead className="text-zinc-500">Date</TableHead>
                      <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((o) => (
                      <TableRow key={o.id} className="border-white/5" data-testid={`order-row-${o.id}`}>
                        <TableCell className="text-zinc-300 text-xs">{o.user_email}</TableCell>
                        <TableCell className="text-white">
                          <span className="font-mono text-violet-400 mr-1">[{o.service_code}]</span>
                          {o.service_name}
                        </TableCell>
                        <TableCell className="text-zinc-400 max-w-[180px] truncate" title={o.target_link}>{o.target_link}</TableCell>
                        <TableCell className="text-zinc-300 font-mono">{o.quantity}</TableCell>
                        <TableCell className="text-zinc-300 font-mono">RM {Number(o.cost).toFixed(2)}</TableCell>
                        <TableCell className="text-zinc-300 font-mono">RM {Number(o.refunded_amount ?? 0).toFixed(2)}</TableCell>
                          <TableCell className="text-zinc-400 font-mono text-xs">{o.provider_order_id || "-"}</TableCell>
                        <TableCell><StatusBadge status={o.status} /></TableCell>
                          <TableCell className="text-zinc-300 font-mono">{o.remains ?? o.provider_remains ?? "-"}</TableCell>
                          <TableCell className="text-zinc-300 font-mono">{o.start_count ?? o.provider_start_count ?? "-"}</TableCell>
                          <TableCell className="text-zinc-300 font-mono">{o.current_count ?? o.provider_current_count ?? "-"}</TableCell>
                        <TableCell className="text-zinc-500 text-xs">{new Date(o.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          {user.role === "admin" && (o.status === "pending" || o.status === "processing") && (
                            <button
                              onClick={() => openOrderAction("cancel", o)}
                              className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                              data-testid={`cancel-order-${o.id}`}
                              title="Cancel order"
                            >
                              <Ban className="w-3.5 h-3.5" />
                              {orderActionLoading && orderActionDlg.order?.id === o.id ? "Cancelling..." : "Cancel"}
                            </button>
                          )}
                          {user.role === "admin" && o.status === "completed" && (
                            <button
                              onClick={() => openOrderAction("refund", o)}
                              className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-amber-300 hover:bg-amber-500/10 transition-colors"
                              data-testid={`refund-order-${o.id}`}
                              title="Refund order"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Refund
                            </button>
                          )}
                          {o.provider_order_id && (
                            <button onClick={() => refreshOrder(o.id)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`refresh-order-${o.id}`}>
                              <RefreshCw className="w-4 h-4 text-violet-400" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {orders.length === 0 && (
                      <TableRow className="border-white/5"><TableCell colSpan={13} className="text-center text-zinc-500 py-8">Tiada order</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <AlertDialog
            open={orderActionDlg.open}
            onOpenChange={(open) => !open && !orderActionLoading && setOrderActionDlg({ open: false, action: null, order: null })}
          >
            <AlertDialogContent className="bg-[#0f0f12] border-white/10 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {orderActionDlg.action === "refund" ? "Refund order?" : "Cancel order?"}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-400">
                  Provider order: {orderActionDlg.order?.provider_order_id || "-"}. Cancel hanya direfund selepas provider mengesahkan status akhir.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={orderActionLoading}>Batal</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(event) => { event.preventDefault(); confirmOrderAction(); }}
                  disabled={orderActionLoading}
                  className="bg-red-600 hover:bg-red-500"
                >
                  {orderActionLoading ? "Cancelling..." : "Sahkan"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* TOPUPS */}
          <TabsContent value="topups" className="mt-4">
            <div className="rounded-2xl bg-[#0f0f12] border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h3 className="font-heading font-bold">Permohonan Top Up</h3>
                <p className="text-xs text-zinc-500 mt-1">Approve untuk auto-tambah baki user.</p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">User</TableHead>
                      <TableHead className="text-zinc-500">Nama Pembayar</TableHead>
                      <TableHead className="text-zinc-500">Amount</TableHead>
                      <TableHead className="text-zinc-500">Resit</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Date</TableHead>
                      <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topups.map((t) => (
                      <TableRow key={t.id} className="border-white/5" data-testid={`topup-row-${t.id}`}>
                        <TableCell className="text-zinc-300 text-xs">{t.user_email}</TableCell>
                        <TableCell className="text-white">{t.name}</TableCell>
                        <TableCell className="text-white font-mono">RM {Number(t.amount).toFixed(2)}</TableCell>
                        <TableCell>
                          <a href={`${API}/files/${t.receipt_path}`} target="_blank" rel="noreferrer" className="text-xs text-violet-400 hover:underline inline-flex items-center gap-1" data-testid={`receipt-${t.id}`}>
                            <ImageIcon className="w-3.5 h-3.5" /> Lihat
                          </a>
                        </TableCell>
                        <TableCell><StatusBadge status={t.status} /></TableCell>
                        <TableCell className="text-zinc-500 text-xs">{new Date(t.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          {t.status === "waiting_payment" && (
                            <>
                              <button onClick={() => approveTopup(t.id)} className="p-2 rounded-lg hover:bg-emerald-500/10" data-testid={`approve-topup-${t.id}`} title="Approve">
                                <Check className="w-4 h-4 text-emerald-400" />
                              </button>
                              <button onClick={() => rejectTopup(t.id)} className="p-2 rounded-lg hover:bg-red-500/10" data-testid={`reject-topup-${t.id}`} title="Reject">
                                <X className="w-4 h-4 text-red-400" />
                              </button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {topups.length === 0 && (
                      <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center text-zinc-500 py-8">Tiada top up</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-4">
            <div className="rounded-2xl bg-[#0f0f12] border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h3 className="font-heading font-bold">Customers</h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Email</TableHead>
                      <TableHead className="text-zinc-500">Nama</TableHead>
                      <TableHead className="text-zinc-500">Phone</TableHead>
                      <TableHead className="text-zinc-500">Baki</TableHead>
                      <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="border-white/5" data-testid={`user-row-${u.id}`}>
                        <TableCell className="text-white">{u.email}</TableCell>
                        <TableCell className="text-zinc-300">{u.name}</TableCell>
                        <TableCell className="text-zinc-400 font-mono">{u.phone || "-"}</TableCell>
                        <TableCell className="text-white font-mono">RM {Number(u.balance).toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <button onClick={() => adjustUserBalance(u)} className="text-xs px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10" data-testid={`adjust-user-${u.id}`}>
                            Adjust Baki
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow className="border-white/5"><TableCell colSpan={5} className="text-center text-zinc-500 py-8">Tiada customer</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* API SETTINGS */}
          <TabsContent value="api" className="mt-4">
            {settings && (
              <div className="grid gap-6 xl:grid-cols-2">
                <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-6">
                  <h3 className="font-heading font-bold mb-1">API Provider 1</h3>
                  <p className="text-xs text-zinc-500 mb-4">Setup connection ke provider SMM utama (tidak diubah).</p>
                  <div className="grid gap-4">
                    <Field label="Provider API URL (contoh: https://provider.com/api/v2)">
                      <Input data-testid="settings-provider-url" value={settings.provider_api_url || ""} onChange={(e) => setSettings({ ...settings, provider_api_url: e.target.value })} className="bg-[#121214] border-white/10 text-white font-mono" placeholder="https://..." />
                    </Field>
                    <Field label="Provider API Key">
                      <Input data-testid="settings-provider-key" type="password" value={settings.provider_api_key || ""} onChange={(e) => setSettings({ ...settings, provider_api_key: e.target.value })} className="bg-[#121214] border-white/10 text-white font-mono" placeholder="Masukkan key baru untuk menukar" autoComplete="new-password" />
                    </Field>
                    <div className="flex gap-2">
                      <button data-testid="save-api-btn" onClick={saveSettings} className="h-11 px-4 rounded-xl bg-violet-600 hover:bg-violet-500 font-semibold">Simpan API</button>
                      <button type="button" onClick={fetchProviderBalance} data-testid="check-provider-balance" className="h-11 px-4 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08]">Check Balance</button>
                    </div>
                    {providerBalance && (
                      <div className="rounded-xl bg-black/40 border border-white/5 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Provider Response</div>
                        <pre className="text-xs text-zinc-300 font-mono whitespace-pre-wrap break-all">{JSON.stringify(providerBalance, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                </div>

                <div className="xl:col-span-2 rounded-2xl bg-[#0f0f12] border border-white/5 p-6">
                  <div className="mt-3 pt-4 border-t border-white/5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-violet-400 mb-2">TNG QR Code</div>
                    {settings.tng_qr_path && (
                      <img src={`${API}/files/${settings.tng_qr_path}`} alt="TNG QR" className="max-w-[220px] rounded-xl border border-white/10 mb-3" data-testid="admin-qr-preview" />
                    )}
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] cursor-pointer text-sm">
                      <input data-testid="upload-qr-input" type="file" accept="image/*" hidden onChange={(e) => uploadQr(e.target.files?.[0])} />
                      <Upload className="w-4 h-4 text-violet-400" />
                      {uploadingQr ? "Uploading..." : (settings.tng_qr_path ? "Tukar QR" : "Upload QR")}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-4">
            {settings && (
              <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-6 max-w-2xl">
                <h3 className="font-heading font-bold mb-4">Settings Laman</h3>
                <div className="grid gap-4">
                  <Field label="WhatsApp Number (cth: 60123456789)">
                    <Input data-testid="settings-wa" value={settings.wa_number} onChange={(e) => setSettings({ ...settings, wa_number: e.target.value })} className="bg-[#121214] border-white/10 text-white" />
                  </Field>
                  <Field label="Telegram Link">
                    <Input data-testid="settings-telegram" value={settings.telegram_link} onChange={(e) => setSettings({ ...settings, telegram_link: e.target.value })} className="bg-[#121214] border-white/10 text-white" />
                  </Field>
                  <Field label="Download App Link">
                    <Input data-testid="settings-download" value={settings.download_app_link} onChange={(e) => setSettings({ ...settings, download_app_link: e.target.value })} className="bg-[#121214] border-white/10 text-white" />
                  </Field>
                  <Field label="Cara Cancel Order">
                    <Textarea data-testid="settings-cancel" value={settings.footer_cancel} onChange={(e) => setSettings({ ...settings, footer_cancel: e.target.value })} className="bg-[#121214] border-white/10 text-white min-h-[80px]" />
                  </Field>
                  <Field label="Cara Refill">
                    <Textarea data-testid="settings-refill" value={settings.footer_refill} onChange={(e) => setSettings({ ...settings, footer_refill: e.target.value })} className="bg-[#121214] border-white/10 text-white min-h-[80px]" />
                  </Field>
                  <Field label="Cara Check Order">
                    <Textarea data-testid="settings-check" value={settings.footer_check_order} onChange={(e) => setSettings({ ...settings, footer_check_order: e.target.value })} className="bg-[#121214] border-white/10 text-white min-h-[80px]" />
                  </Field>
                  <button data-testid="save-settings-btn" onClick={saveSettings} className="h-11 rounded-xl bg-violet-600 hover:bg-violet-500 font-semibold">Simpan Settings</button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ACCOUNT */}
          <TabsContent value="account" className="mt-4">
            <ChangePasswordForm />
          </TabsContent>
        </Tabs>
          </section>
        </div>
      </main>

      {/* Category Dialog */}
      <Dialog open={catDlg.open} onOpenChange={(o) => setCatDlg({ ...catDlg, open: o })}>
        <DialogContent className="bg-[#0f0f12] border-white/10 text-white">
          <DialogHeader><DialogTitle>{catDlg.edit ? "Edit Kategori" : "Tambah Kategori"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Field label="Nama"><Input data-testid="cat-name-input" value={catDlg.data.name} onChange={(e) => setCatDlg({ ...catDlg, data: { ...catDlg.data, name: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
            <Field label="Slug"><Input data-testid="cat-slug-input" value={catDlg.data.slug} onChange={(e) => setCatDlg({ ...catDlg, data: { ...catDlg.data, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") } })} className="bg-[#121214] border-white/10 font-mono" /></Field>
            <Field label="Icon">
              <Select value={catDlg.data.icon} onValueChange={(v) => setCatDlg({ ...catDlg, data: { ...catDlg.data, icon: v } })}>
                <SelectTrigger className="bg-[#121214] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121214] border-white/10 text-white">
                  {iconOptions.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Order"><Input type="number" value={catDlg.data.order} onChange={(e) => setCatDlg({ ...catDlg, data: { ...catDlg.data, order: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
          </div>
          <DialogFooter><button data-testid="cat-save-btn" onClick={saveCat} className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500">Simpan</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={svcDlg.open} onOpenChange={(o) => setSvcDlg({ ...svcDlg, open: o })}>
        <DialogContent className="bg-[#0f0f12] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{svcDlg.edit ? "Edit Servis" : "Tambah Servis"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service ID (nombor)"><Input type="number" data-testid="svc-code-input" value={svcDlg.data.service_code} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, service_code: e.target.value } })} className="bg-[#121214] border-white/10 font-mono" /></Field>
            <Field label="Kategori">
              <Select value={svcDlg.data.category_id} onValueChange={(v) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, category_id: v } })}>
                <SelectTrigger className="bg-[#121214] border-white/10"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                <SelectContent className="bg-[#121214] border-white/10 text-white">
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2"><Field label="Nama Servis"><Input data-testid="svc-name-input" value={svcDlg.data.name} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, name: e.target.value } })} className="bg-[#121214] border-white/10" /></Field></div>
            <Field label="Speed"><Input value={svcDlg.data.speed} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, speed: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
            <Field label="Quality"><Input value={svcDlg.data.quality} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, quality: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
            <Field label="Geo"><Input value={svcDlg.data.geo} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, geo: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
            <Field label="Start Time"><Input value={svcDlg.data.start_time} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, start_time: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
            <Field label="Min Qty"><Input type="number" value={svcDlg.data.min_qty} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, min_qty: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
            <Field label="Max Qty"><Input type="number" value={svcDlg.data.max_qty} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, max_qty: e.target.value } })} className="bg-[#121214] border-white/10" /></Field>
            <div className="col-span-2"><Field label="Harga per 1000 (RM)"><Input type="number" step="0.01" value={svcDlg.data.price_per_1000} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, price_per_1000: e.target.value } })} className="bg-[#121214] border-white/10" /></Field></div>
            <div className="col-span-2"><Field label="Provider Service ID (nombor dari SMM provider)"><Input data-testid="svc-provider-id-input" value={svcDlg.data.provider_service_id || ""} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, provider_service_id: e.target.value } })} className="bg-[#121214] border-white/10 font-mono" placeholder="cth: 1234" /></Field></div>
            <div className="col-span-2"><Field label="Description"><Textarea value={svcDlg.data.description} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, description: e.target.value } })} className="bg-[#121214] border-white/10 min-h-[80px]" /></Field></div>
            <div className="col-span-2"><Field label="Warning"><Textarea value={svcDlg.data.warning} onChange={(e) => setSvcDlg({ ...svcDlg, data: { ...svcDlg.data, warning: e.target.value } })} className="bg-[#121214] border-white/10 min-h-[60px]" /></Field></div>
          </div>
          <DialogFooter><button data-testid="svc-save-btn" onClick={saveSvc} className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500">Simpan</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AdminNavButton = ({ Icon, label, value, activeTab, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    data-testid={`admin-nav-${value}`}
    className={`inline-flex min-h-11 items-center gap-2 rounded-xl px-3 py-2 text-left text-xs font-semibold transition-colors lg:w-full ${activeTab === value ? "bg-violet-600 text-white" : "text-zinc-400 hover:bg-white/[0.05] hover:text-white"}`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span>{label}</span>
  </button>
);

const StatCard = ({ Icon, label, value, mono }) => (
  <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 grid place-items-center">
      <Icon className="w-5 h-5 text-violet-400" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-xl font-bold ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-zinc-400 mb-1">{label}</span>
    {children}
  </label>
);
